import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { Shift } from "@enums/shift.enum";
import { CheckGroupPermissions } from "@helpers/checkGroupPermissions";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { GenerateStaffAttendanceReportCsv } from "@interfaces/generateStaffAttendanceReportCsv.interface";
import { LogExport } from "@interfaces/logExport.interface";
import { Site } from "@interfaces/site.interface";
import { UserProfile } from "@interfaces/userProfile.interface";
import { AppSettingsService } from "@services/appSettings.service";
import { ExportLogService } from "@services/exportLog.service";
import { SiteService } from "@services/site.service";
import { StaffAttendanceService } from "@services/staffAttendance.service";
import { UserProfileService } from "@services/userProfile.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "userProfiles",
	styleUrls: ["userProfiles.css"],
	templateUrl: "userProfiles.html"
})
export class UserProfilesAdminComponent {
	deleteEnabled = false;
	editEnabled = true;
	gridLoading = false;
	headerPrimaryButtons: AdminHeaderButton[] = [];
	headerSecondaryButtons: AdminHeaderButton[] = [];
	headerTertiaryButtons: AdminHeaderButton[] = [];
	isSuperAdmin = false;
	itemType = "User"
	itemUrl: string = location.href.split("/")[3] + "/" + location.href.split("/")[4];
	items: UserProfile[] = [];
	popupAddButtonOptions: any;
	popupCloseButtonOptions: any = { onClick: () => this.closePopup(), text: "Close" };
	popupGenerateStaffAttendanceCsv: GenerateStaffAttendanceReportCsv = new GenerateStaffAttendanceReportCsv();
	popupTitle = "";
	popupVisible = false;
	shifts: any;
	showInactive = false;
	sites: Site[] = [];
	title: string;
	
	constructor(private appSettingsService: AppSettingsService, private toastService: ToastService, private router: Router, private exportLogService: ExportLogService, private siteService: SiteService, private staffAttendanceService: StaffAttendanceService, private itemService: UserProfileService) {
		this.isSuperAdmin = CheckGroupPermissions(appSettingsService, "Super Admin");
		this.shifts = GetPropertiesFromEnum(Shift);
		this.headerPrimaryButtons.push(
			{ icon: "fal fa-plus", method: "addNewItem", text: `New ${this.itemType}` },
			{ icon: "fal fa-download", method: "generateAndDownloadStaffAttendanceCSVPopup", text: "Staff Attendance Report" }
		);
		this.headerSecondaryButtons.push({ icon: "fal fa-ball-pile", method: "gotoGroups", text: "Groups" });
		this.title = `${this.itemType}s`;
		this.deleteRow = this.deleteRow.bind(this);
		this.editRow = this.editRow.bind(this);
		this.viewRow = this.viewRow.bind(this);
		this.getItems(this.showInactive);
		this.getSites();
	}

	addNewItem() {
		this.router.navigate([`${this.itemUrl}/create`]);
	}

	buildButtons() {
		this.headerTertiaryButtons = [];
		if (this.isSuperAdmin === true) {
			if (this.showInactive === true) {
				this.headerTertiaryButtons = [{ method: "showActiveProfiles", text: "Show Active Users" }];
			}
			else {
				this.headerTertiaryButtons = [{ method: "showInactiveProfiles", text: "Show Inactive Users" }];
			}
		}
	}

	closePopup() {
		this.popupVisible = false;
	}

	deleteRow(e: any) {
		const result = confirm("Are you sure you want to delete this item?");
		if(result)
		{
			this.itemService.deleteSingleById(e.row.data.id)
				.subscribe(
					() => {
						this.toastService.success(this.itemType + " deleted!");
						this.getItems(this.showInactive);
					},
					(err) => {
						console.log(err);
						this.toastService.error("Something Went Wrong!");
					},
				);
		}
	}

	editRow(e: any) {
		this.router.navigate([`${this.itemUrl}/${e.row.data.id}_edit`]);
	}

	generateAndDownloadStaffAttendanceCSV() {
		this.staffAttendanceService.generateReport(this.popupGenerateStaffAttendanceCsv).subscribe(
			(response: any) =>{
				const dataType = response.type;
				const binaryData = [];
				binaryData.push(response);
				const downloadLink = document.createElement("a");
				downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, {type: dataType}));
				downloadLink.setAttribute("download", "Staff_Attendance_Report.csv");
				document.body.appendChild(downloadLink);
				downloadLink.click();
				this.closePopup();
			}
		);
	}

	generateAndDownloadStaffAttendanceCSVPopup() {
		this.popupGenerateStaffAttendanceCsv = new GenerateStaffAttendanceReportCsv();
		this.popupTitle = "Generate Attendance Report";
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.generateAndDownloadStaffAttendanceCSV(), text: "Generate Attendance Report" };
	}

	getItems(showInactive: boolean) {
		this.gridLoading = true;
		this.items = [];
		if (showInactive === true) {
			this.itemService.getInactiveUsers()
				.subscribe(
					(res: any) => {
						this.items = res.response,
						this.gridLoading = false;
					},
					(err: any) => {
						console.log(err);
						this.toastService.error("Something Went Wrong!");
					},
				);
		} else {
			this.itemService.getActiveUsers()
				.subscribe(
					(res: any) => {
						this.items = res.response,
						this.gridLoading = false;
					},
					(err: any) => {
						console.log(err);
						this.toastService.error("Something Went Wrong!");
					},
				);
		}
		this.buildButtons();
	}

	getSites() {
		this.siteService.getAll()
			.subscribe(
				(res: any) => this.sites = res.response.items,
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	gotoGroups() {
		this.router.navigate(["admin/groups"]);
	}

	headerButtonClick(method: any) {
		// @ts-ignore // Required to be able to call the method directly from the variable
		if (this[method]) this[method]();
	}

	logExport() {
		const logExport = new LogExport();
		logExport.gridName = "People";
		this.exportLogService.logExport(logExport)
			.subscribe(
				() => console.log("Export Logged"),
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
					return false;
				},
			);
	}

	showActiveProfiles() {
		this.showInactive = false;
		this.getItems(false);
	}

	showInactiveProfiles() {
		this.showInactive = true;
		this.getItems(true);
	}

	viewRow(e: any) {
		this.router.navigate([`${this.itemUrl}/${e.row.data.id}`]);
	}
}